import { DocumentRecordStatus } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION } from '../queries';

interface UnpublishUpdateProps {
  isOpen: boolean;
  updateId: string | null;
  refetch: () => void;
  onClose: () => void;
}

const UnpublishUpdate = ({ isOpen, updateId, refetch, onClose }: UnpublishUpdateProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateDocumentRecordStatus, { loading: updateDocumentRecordStatusLoading }] = useMutation(
    STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION,
    {
      onError: (error) => {
        console.error(error);
        setErrorMessage(defaultErrorMessage);
      }
    }
  );

  const saveValue = () => {
    setErrorMessage(null);

    if (!updateId) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    updateDocumentRecordStatus({
      variables: {
        data: {
          id: updateId,
          status: DocumentRecordStatus.Draft
        }
      },
      onCompleted: () => {
        refetch();
        onClose();
      }
    });
  };

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{ name: 'Unpublish', onClick: saveValue, disabled: updateDocumentRecordStatusLoading }}
      isUseOutsideClick={!updateDocumentRecordStatusLoading}
      showCloseIcon={!updateDocumentRecordStatusLoading}
      showCancelButton={!updateDocumentRecordStatusLoading}
    >
      <ModalBody>
        <ModalTitle>Unpublish Update</ModalTitle>
        {updateDocumentRecordStatusLoading ? (
          <ModalLoader />
        ) : (
          <>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>This update will be available to edit and publish at a later date.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default UnpublishUpdate;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 35px 0;
`;

const ModalTitle = styled.div`
  text-align: center;
  font-size: 33px;
  font-weight: 400;
  line-height: 46.2px;
  color: ${themeVar('font.base')};
`;

const ModalDescription = styled.div`
  font-size: 19px;
  line-height: 27px;
  text-align: center;
  line-height: 26.6px;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
